import React, { useState, useEffect, useCallback, useRef } from 'react';
import Coachmark from './tutorial/Coachmark';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Mining.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { authButtonStyle, authStatPanelStyle } from '../constants/authStyles';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { formatDateTime } from '../utils/localeFormat';
import { useCharacterOptional } from '../context/CharacterContext';
import { useGameConfig } from '../context/GameConfigContext';
import { MINING_MIN_LEVEL } from '../constants/mining';
import { meetsMinLevel } from '../utils/levelRequirement';
import { canPlayCharacterSfx, playSfxFile } from '../utils/audioSettings';
import { ProfessionBadge } from './ProfessionPanels';

const TEXTBOX_FRAME = '/image/menu/textbox1uzun.png';
const CATCH_FLY_MS = 900;

const MINING_BG = '/image/mining/bg.png';
const MINING_IDLE_CHAR = '/image/mining/stop.png';
const MINING_PICKAXE_SOUND = '/music/kazmasesi.mp3';

const SCENE_IDLE = 'idle';
const SCENE_MINING = 'mining';
const SCENE_CLAIM = 'claim';

const SCENE_IMAGES = {
  [SCENE_IDLE]: MINING_IDLE_CHAR,
  [SCENE_MINING]: '/image/mining/miner.png',
  [SCENE_CLAIM]: '/image/mining/madenaldi.png',
};

const SCENE_BACKGROUNDS = {
  [SCENE_IDLE]: MINING_BG,
  [SCENE_MINING]: MINING_BG,
  [SCENE_CLAIM]: MINING_BG,
};

const SPRITE_SCENES = new Set([SCENE_MINING, SCENE_CLAIM]);
const SPRITE_FRAME_W = 300;
const SPRITE_FRAME_H = 200;
const SPRITE_FRAMES = 5;
const SPRITE_SHEET_W = SPRITE_FRAME_W * SPRITE_FRAMES;
const SPRITE_FPS = 5;
const SPRITE_FRAME_MS = 1000 / SPRITE_FPS;
const SPRITE_LAST_FRAME_HOLD_MS = 1500;

const MINING_IMAGE_URLS = [
  ...new Set([
    ...Object.values(SCENE_IMAGES),
    ...Object.values(SCENE_BACKGROUNDS),
  ]),
];

const miningImageCache = new Map();

const waitForNextPaint = () => new Promise((resolve) => {
  requestAnimationFrame(() => requestAnimationFrame(resolve));
});

const ensureMiningImagesLoaded = (urls) => {
  const list = (Array.isArray(urls) ? urls : [urls]).filter(Boolean);
  return Promise.all(list.map((url) => {
    if (miningImageCache.get(url) === 'loaded') {
      return Promise.resolve();
    }
    const pending = miningImageCache.get(url);
    if (pending && typeof pending.then === 'function') {
      return pending;
    }
    const promise = new Promise((resolve) => {
      const img = new Image();
      const finish = async () => {
        try {
          if (typeof img.decode === 'function') {
            await img.decode();
          }
        } catch (_) {
          // ignore decode errors
        }
        miningImageCache.set(url, 'loaded');
        resolve();
      };
      img.onload = () => { finish(); };
      img.onerror = () => {
        miningImageCache.set(url, 'loaded');
        resolve();
      };
      img.src = url;
    });
    miningImageCache.set(url, promise);
    return promise;
  }));
};

const spriteSceneAssets = (nextScene) => [
  SCENE_BACKGROUNDS[nextScene],
  SCENE_IMAGES[nextScene],
];

const oreImageUrlForId = (oreId) => {
  if (!oreId || typeof oreId !== 'string') return null;
  const id = oreId.trim().toLowerCase();
  if (!id) return null;
  return `/image/others/${id}.png`;
};

const oreImageUrlForDrop = (drop, mineConfig) => {
  const fromOreId = oreImageUrlForId(drop?.oreId);
  if (fromOreId) return fromOreId;
  const otherItemId = Number(drop?.otherItemId);
  if (!otherItemId) return null;
  const ores = mineConfig?.ores;
  if (Array.isArray(ores)) {
    const match = ores.find((ore) => Number(ore.otherItemId) === otherItemId);
    if (match?.id) {
      return oreImageUrlForId(match.id);
    }
  }
  return `/image/others/${otherItemId}.png`;
};

const formatSeconds = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
  }
  return `${m}:${String(r).padStart(2, '0')}`;
};

const formatBookRemaining = (untilUnix) => {
  if (!untilUnix || untilUnix <= Math.floor(Date.now() / 1000)) return null;
  return formatDateTime(untilUnix * 1000, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const Mining = () => {
  const navigate = useNavigate();
  const { t: ts } = useTranslation('selection');
  const { t: tc } = useTranslation('common');
  const username = localStorage.getItem('username');
  const { setError, setStatus } = usePopupMessage();
  const characterCtx = useCharacterOptional();
  const { gelistirmeConsole } = useGameConfig();
  const setDepotItemCount = characterCtx?.setDepotItemCount;
  const characterLevel = characterCtx?.character?.level ?? 0;
  const characterLoading = characterCtx?.loading;
  const miningLevelOk = meetsMinLevel(characterLevel, MINING_MIN_LEVEL, gelistirmeConsole);
  const refreshCharacter = characterCtx?.refreshCharacter;

  const [config, setConfig] = useState(null);
  const [hasPickaxe, setHasPickaxe] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [pickaxeEnh, setPickaxeEnh] = useState(0);
  const [oreMined, setOreMined] = useState(0);
  const [sessionActive, setSessionActive] = useState(false);
  const [sessionPaused, setSessionPaused] = useState(false);
  const [sessionSecondsLeft, setSessionSecondsLeft] = useState(0);
  const [sessionMaxSeconds, setSessionMaxSeconds] = useState(3600);
  const [manualHits, setManualHits] = useState(0);
  const [afkHitsPreview, setAfkHitsPreview] = useState(0);
  const [totalHitsPreview, setTotalHitsPreview] = useState(0);
  const [minerBookUntil, setMinerBookUntil] = useState(0);
  const [masterMinerBookUntil, setMasterMinerBookUntil] = useState(0);
  const [busy, setBusy] = useState(false);
  const [lastClaim, setLastClaim] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [scene, setScene] = useState(SCENE_IDLE);
  const [spriteFrame, setSpriteFrame] = useState(0);
  const [underlayScene, setUnderlayScene] = useState(null);
  const [dropNotifications, setDropNotifications] = useState([]);
  const [catchFlyItems, setCatchFlyItems] = useState([]);
  const [clickCooldownUntil, setClickCooldownUntil] = useState(0);
  const [clickCooldownSec, setClickCooldownSec] = useState(0);

  const computeClickCooldownMs = useCallback((pickaxeLevel) => {
    const at0 = config?.manualClickCooldownMsAt0 ?? 15000;
    const at9 = config?.manualClickCooldownMsAt9 ?? 5000;
    const level = Math.min(9, Math.max(0, Number(pickaxeLevel) || 0));
    return Math.round(at0 + (at9 - at0) * (level / 9));
  }, [config]);

  const applyClickCooldown = useCallback((remainingMs, fullMs) => {
    const remain = Number(remainingMs) || 0;
    const full = Number(fullMs) || 0;
    if (remain > 0) {
      setClickCooldownUntil(Date.now() + remain);
      return;
    }
    if (full > 0) {
      setClickCooldownUntil(Date.now() + full);
    }
  }, []);
  const timerRef = useRef(null);
  const mineVisualRef = useRef(null);
  const inventoryBtnRef = useRef(null);
  const sceneRef = useRef(SCENE_IDLE);
  const spriteTimerRef = useRef(null);
  const spriteHoldTimerRef = useRef(null);
  const spriteResolveRef = useRef(null);
  const miningLoopRef = useRef(null);
  const pickaxeAudioRef = useRef(null);
  const claimInProgressRef = useRef(false);
  const suppressNoPickaxeWarnRef = useRef(false);
  const dropNotificationTimeoutsRef = useRef({});
  const catchFlyTimeoutsRef = useRef({});

  useEffect(() => {
    ensureMiningImagesLoaded(MINING_IMAGE_URLS);
  }, []);

  useEffect(() => {
    const audio = new Audio(MINING_PICKAXE_SOUND);
    audio.preload = 'auto';
    pickaxeAudioRef.current = audio;
    return () => {
      audio.pause();
      pickaxeAudioRef.current = null;
    };
  }, []);

  const playPickaxeSound = useCallback(() => {
    if (!canPlayCharacterSfx()) return;
    const audio = pickaxeAudioRef.current;
    if (!audio) {
      playSfxFile(MINING_PICKAXE_SOUND, 0.75, 'character');
      return;
    }
    try {
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } catch (_) {
      // ignore
    }
  }, []);

  useEffect(() => {
    sceneRef.current = scene;
  }, [scene]);

  useEffect(() => {
    if (clickCooldownUntil <= Date.now()) {
      setClickCooldownSec(0);
      return undefined;
    }
    const tick = () => {
      const rem = Math.ceil((clickCooldownUntil - Date.now()) / 1000);
      setClickCooldownSec(rem > 0 ? rem : 0);
    };
    tick();
    const id = setInterval(tick, 200);
    return () => clearInterval(id);
  }, [clickCooldownUntil]);

  const stopSpritePlayback = useCallback(() => {
    if (spriteTimerRef.current) {
      clearInterval(spriteTimerRef.current);
      spriteTimerRef.current = null;
    }
    if (spriteHoldTimerRef.current) {
      clearTimeout(spriteHoldTimerRef.current);
      spriteHoldTimerRef.current = null;
    }
    if (miningLoopRef.current) {
      clearInterval(miningLoopRef.current);
      miningLoopRef.current = null;
    }
    if (spriteResolveRef.current) {
      const resolve = spriteResolveRef.current;
      spriteResolveRef.current = null;
      resolve();
    }
  }, []);

  const runSpriteAnimation = useCallback(
    ({ holdLastMs = 0, loop = false } = {}) =>
      new Promise((resolve) => {
        stopSpritePlayback();
        let frame = 0;
        spriteResolveRef.current = resolve;

        const finish = () => {
          spriteResolveRef.current = null;
          resolve();
        };

        const holdThenFinish = () => {
          if (holdLastMs > 0) {
            setSpriteFrame(SPRITE_FRAMES - 1);
            spriteHoldTimerRef.current = setTimeout(() => {
              spriteHoldTimerRef.current = null;
              finish();
            }, holdLastMs);
            return;
          }
          finish();
        };

        setSpriteFrame(0);
        if (loop) {
          miningLoopRef.current = setInterval(() => {
            const nextFrame = (frame + 1) % SPRITE_FRAMES;
            if (nextFrame === SPRITE_FRAMES - 1) {
              playPickaxeSound();
            }
            frame = nextFrame;
            setSpriteFrame(frame);
          }, SPRITE_FRAME_MS);
          return;
        }

        spriteTimerRef.current = setInterval(() => {
          frame += 1;
          if (frame >= SPRITE_FRAMES) {
            if (spriteTimerRef.current) {
              clearInterval(spriteTimerRef.current);
              spriteTimerRef.current = null;
            }
            holdThenFinish();
            return;
          }
          setSpriteFrame(frame);
        }, SPRITE_FRAME_MS);
      }),
    [stopSpritePlayback, playPickaxeSound]
  );

  const transitionToSpriteScene = useCallback(async (nextScene) => {
    const currentScene = sceneRef.current;
    if (!SPRITE_SCENES.has(currentScene) && SPRITE_SCENES.has(nextScene)) {
      setUnderlayScene(currentScene);
    }
    await ensureMiningImagesLoaded(spriteSceneAssets(nextScene));
    await waitForNextPaint();
    setScene(nextScene);
    setSpriteFrame(0);
    await waitForNextPaint();
    setUnderlayScene(null);
  }, []);

  const setIdleSceneWhenReady = useCallback(
    () => ensureMiningImagesLoaded(SCENE_IMAGES[SCENE_IDLE]).then(() => {
      stopSpritePlayback();
      setScene(SCENE_IDLE);
      setSpriteFrame(0);
    }),
    [stopSpritePlayback]
  );

  const startMiningLoopScene = useCallback(async () => {
    await transitionToSpriteScene(SCENE_MINING);
    await runSpriteAnimation({ loop: true });
  }, [transitionToSpriteScene, runSpriteAnimation]);

  useEffect(() => () => stopSpritePlayback(), [stopSpritePlayback]);

  useEffect(() => {
    const timeouts = dropNotificationTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  useEffect(() => {
    const timeouts = catchFlyTimeoutsRef.current;
    return () => {
      Object.values(timeouts).forEach((timeoutId) => clearTimeout(timeoutId));
    };
  }, []);

  const addDropNotification = useCallback((message) => {
    if (!message) return;
    const id = Date.now() + Math.random();
    setDropNotifications((prev) => {
      const next = [...prev, { id, message }];
      if (next.length <= 3) return next;
      const removed = next.shift();
      if (removed?.id && dropNotificationTimeoutsRef.current[removed.id]) {
        clearTimeout(dropNotificationTimeoutsRef.current[removed.id]);
        delete dropNotificationTimeoutsRef.current[removed.id];
      }
      return next;
    });
    const timeoutId = setTimeout(() => {
      setDropNotifications((prev) => prev.filter((n) => n.id !== id));
      delete dropNotificationTimeoutsRef.current[id];
    }, 3000);
    dropNotificationTimeoutsRef.current[id] = timeoutId;
  }, []);

  const spawnCatchFlyAnimation = useCallback((dropOrOreId, delayMs = 0) => {
    const imageUrl = typeof dropOrOreId === 'object'
      ? oreImageUrlForDrop(dropOrOreId, config)
      : oreImageUrlForId(dropOrOreId);
    const fromEl = mineVisualRef.current;
    const toEl = inventoryBtnRef.current;
    if (!imageUrl || !fromEl || !toEl) return;

    const spawn = () => {
      const fromRect = fromEl.getBoundingClientRect();
      const toRect = toEl.getBoundingClientRect();
      const startX = fromRect.left + fromRect.width / 2;
      const startY = fromRect.bottom;
      const endX = toRect.left + toRect.width / 2;
      const endY = toRect.top + toRect.height / 2;
      const id = Date.now() + Math.random();

      setCatchFlyItems((prev) => [
        ...prev,
        {
          id,
          imageUrl,
          startX,
          startY,
          deltaX: endX - startX,
          deltaY: endY - startY,
        },
      ]);

      const timeoutId = setTimeout(() => {
        setCatchFlyItems((prev) => prev.filter((item) => item.id !== id));
        delete catchFlyTimeoutsRef.current[id];
      }, CATCH_FLY_MS + 50);
      catchFlyTimeoutsRef.current[id] = timeoutId;
    };

    if (delayMs > 0) {
      setTimeout(spawn, delayMs);
    } else {
      spawn();
    }
  }, [config]);

  const playClaimRewardFeedback = useCallback((drops) => {
    const list = Array.isArray(drops) ? drops.filter((drop) => drop?.count > 0) : [];
    list.forEach((drop, index) => {
      spawnCatchFlyAnimation(drop, index * 120);
      if (drop?.name && drop?.count) {
        addDropNotification(ts('miningDropNotify', { name: drop.name, count: drop.count }));
      }
    });
    if (list.length === 0) return Promise.resolve();
    const waitMs = CATCH_FLY_MS + Math.max(0, list.length - 1) * 120 + 80;
    return new Promise((resolve) => {
      setTimeout(resolve, waitMs);
    });
  }, [addDropNotification, spawnCatchFlyAnimation, ts]);

  const fetchStatus = useCallback(async () => {
    if (!username) return;
    try {
      const res = await axios.get(`/api/mining/status?username=${encodeURIComponent(username)}`);
      if (!res.data?.success) return;
      setHasPickaxe(!!res.data.hasPickaxe);
      setPickaxeEnh(res.data.pickaxeEnhancement ?? 0);
      setOreMined(res.data.oreMined ?? 0);
      setSessionActive(!!res.data.sessionActive);
      setSessionPaused(!!res.data.sessionPaused);
      setSessionSecondsLeft(res.data.sessionSecondsLeft ?? 0);
      setSessionMaxSeconds(res.data.sessionMaxSeconds ?? 3600);
      setManualHits(res.data.sessionManualHits ?? 0);
      setAfkHitsPreview(res.data.afkHitsPreview ?? 0);
      setTotalHitsPreview(res.data.totalHitsPreview ?? 0);
      setMinerBookUntil(res.data.minerBookUntil ?? 0);
      setMasterMinerBookUntil(res.data.masterMinerBookUntil ?? 0);
      if (res.data.sessionActive && (res.data.manualClickCooldownRemainingMs ?? 0) > 0) {
        setClickCooldownUntil(Date.now() + res.data.manualClickCooldownRemainingMs);
      } else if (!res.data.sessionActive) {
        setClickCooldownUntil(0);
      }
    } catch (err) {
      console.error('Mining status error:', err);
    } finally {
      setStatusLoaded(true);
    }
  }, [username]);

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    axios.get('/api/mining/config')
      .then((res) => {
        if (res.data?.success) setConfig(res.data.config);
      })
      .catch(() => {});
    fetchStatus();
  }, [username, navigate, fetchStatus]);

  useEffect(() => {
    if (claimInProgressRef.current) return undefined;
    if (!sessionActive) {
      if (timerRef.current) clearInterval(timerRef.current);
      setIdleSceneWhenReady();
      return undefined;
    }
    if (sessionPaused) {
      setIdleSceneWhenReady();
    } else {
      startMiningLoopScene();
    }
    timerRef.current = setInterval(() => {
      fetchStatus();
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionActive, sessionPaused, fetchStatus, setIdleSceneWhenReady, startMiningLoopScene]);

  useEffect(() => {
    const onFocus = () => { fetchStatus(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchStatus]);

  useEffect(() => {
    if (!characterLoading && characterLevel > 0 && !miningLevelOk) {
      setError(ts('workMiningLocked', { level: MINING_MIN_LEVEL }));
    }
  }, [characterLevel, characterLoading, miningLevelOk, setError, ts]);

  useEffect(() => {
    if (!statusLoaded || characterLoading || !miningLevelOk) return;
    if (suppressNoPickaxeWarnRef.current) {
      suppressNoPickaxeWarnRef.current = false;
      return;
    }
    if (!hasPickaxe && !sessionActive) {
      setError(ts('miningNoPickaxe'));
    }
  }, [statusLoaded, hasPickaxe, sessionActive, characterLoading, miningLevelOk, setError, ts]);

  const handleStart = async () => {
    if (!username || busy) return;
    if (!miningLevelOk) {
      setError(ts('workMiningLocked', { level: MINING_MIN_LEVEL }));
      return;
    }
    setBusy(true);
    setStatusMsg('');
    setLastClaim(null);
    try {
      const res = await axios.post('/api/mining/start', { username });
      if (res.data?.success) {
        setStatus(res.data.message || ts('miningStartSuccess'));
        setSessionActive(true);
        setSessionSecondsLeft(res.data.sessionSecondsLeft ?? sessionMaxSeconds);
        setManualHits(0);
        setStatusMsg(ts('miningSessionActive'));
        await fetchStatus();
      } else {
        setError(res.data?.message || ts('miningStartFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || ts('miningStartFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleClick = async () => {
    if (!username || busy || !sessionActive) return;
    if (clickCooldownUntil > Date.now()) return;
    playPickaxeSound();
    setBusy(true);
    try {
      const res = await axios.post('/api/mining/click', { username });
      if (res.data?.success) {
        setManualHits(res.data.manualHits ?? 0);
        setAfkHitsPreview(res.data.afkHitsPreview ?? 0);
        setTotalHitsPreview(res.data.totalHitsPreview ?? 0);
        applyClickCooldown(
          res.data.manualClickCooldownRemainingMs,
          res.data.manualClickCooldownMs ?? computeClickCooldownMs(pickaxeEnh)
        );
        setStatusMsg(ts('miningClickOk', { hits: res.data.manualHits ?? 0 }));
      } else if (res.data?.cooldownBlocked) {
        applyClickCooldown(
          res.data.manualClickCooldownRemainingMs,
          res.data.manualClickCooldownMs ?? computeClickCooldownMs(pickaxeEnh)
        );
        if (res.data.message) {
          setStatusMsg(res.data.message);
        }
      } else {
        setError(res.data?.message || ts('miningClickFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || ts('miningClickFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleClaim = async () => {
    if (!username || busy || !sessionActive) return;
    setBusy(true);
    claimInProgressRef.current = true;
    stopSpritePlayback();
    try {
      const res = await axios.post('/api/mining/claim', { username });
      if (res.data?.success) {
        await transitionToSpriteScene(SCENE_CLAIM);
        await runSpriteAnimation({ holdLastMs: SPRITE_LAST_FRAME_HOLD_MS });

        const drops = res.data.drops || [];

        if (typeof res.data?.depotItemCount === 'number') {
          setDepotItemCount?.(res.data.depotItemCount);
        }

        suppressNoPickaxeWarnRef.current = true;
        setStatus(res.data.message || ts('miningClaimSuccess'));
        setLastClaim(res.data);
        setOreMined(res.data.oreMined ?? oreMined);
        setStatusMsg(ts('miningClaimDone'));
        const flyWait = playClaimRewardFeedback(drops);
        setSessionActive(false);
        setSessionPaused(false);
        setSessionSecondsLeft(0);
        setManualHits(0);
        setAfkHitsPreview(0);
        setTotalHitsPreview(0);
        await flyWait;
        await setIdleSceneWhenReady();
        await fetchStatus();
        if (refreshCharacter) await refreshCharacter().catch(() => {});
      } else {
        setError(res.data?.message || ts('miningClaimFailed'));
        if (sessionActive) {
          await startMiningLoopScene();
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || ts('miningClaimFailed'));
      if (sessionActive) {
        await startMiningLoopScene();
      }
    } finally {
      claimInProgressRef.current = false;
      setBusy(false);
    }
  };

  const renderToolbar = () => (
    <AuthSelectionToolbar
      className="mining-toolbar"
      onBack={() => navigate('/work')}
      backLabel={tc('back')}
      buttonStyle={authButtonStyle}
      title={ts('workMining')}
      subtitle={ts('miningSubtitle')}
      leadingExtra={(
        <button
          ref={inventoryBtnRef}
          type="button"
          className="auth-button auth-button--compact"
          style={authButtonStyle}
          onClick={() => navigate('/inventory', { state: { from: '/mining' } })}
        >
          {ts('menuInventory')}
        </button>
      )}
    />
  );

  const renderMineVisual = () => {
    const isSpriteScene = SPRITE_SCENES.has(scene);
    const sceneImage = SCENE_IMAGES[scene] || SCENE_IMAGES[SCENE_IDLE];
    const sceneBg = SCENE_BACKGROUNDS[scene] || MINING_BG;
    const underlayBg = underlayScene
      ? (SCENE_BACKGROUNDS[underlayScene] || SCENE_IMAGES[underlayScene])
      : null;
    const spriteBackgroundPosition = isSpriteScene
      ? `-${spriteFrame * SPRITE_FRAME_W}px 0`
      : undefined;

    return (
      <div
        ref={mineVisualRef}
        className="mining-cave-visual mining-cave-visual--layered"
      >
        {underlayBg && (
          <div
            className="mining-cave-visual__bg mining-cave-visual__bg--underlay"
            style={{ backgroundImage: `url("${underlayBg}")` }}
          />
        )}
        <div
          className="mining-cave-visual__bg"
          style={{ backgroundImage: `url("${sceneBg}")` }}
        />
        {isSpriteScene ? (
          <div
            className="mining-cave-visual__sprite"
            style={{
              backgroundImage: `url("${sceneImage}")`,
              backgroundSize: `${SPRITE_SHEET_W}px ${SPRITE_FRAME_H}px`,
              backgroundPosition: spriteBackgroundPosition,
            }}
          />
        ) : (
          <div
            className="mining-cave-visual__character"
            style={{ backgroundImage: `url("${sceneImage}")` }}
          />
        )}
      </div>
    );
  };

  const minerBookExpiry = formatBookRemaining(minerBookUntil);
  const masterBookExpiry = formatBookRemaining(masterMinerBookUntil);
  const smeltInputCount = config?.smelt?.orePerAttempt ?? config?.smelt?.inputCount ?? 10;

  const canMineNow = hasPickaxe && sessionActive && !sessionPaused;

  if (characterLoading || !miningLevelOk) {
    return (
      <div className="auth-container auth-container--selection mining-page">
      <Coachmark
        tourId="mining"
        steps={[
          { selector: '.mining-actions', textKey: 'miningActions' },
      ]}
      />
        <AuthLayeredCard className="mining-card">
          {renderToolbar()}
          <div className="auth-selection-body mining-body">
            <p className="mining-warn">
              {ts('workMiningLocked', { level: MINING_MIN_LEVEL })}
            </p>
          </div>
        </AuthLayeredCard>
      </div>
    );
  }

  return (
    <div className="auth-container auth-container--selection mining-page">
      <Coachmark
        tourId="mining"
        steps={[
          { selector: '.mining-actions', textKey: 'miningActions' },
      ]}
      />
      {catchFlyItems.map((item) => (
        <img
          key={item.id}
          className="mining-catch-fly"
          src={item.imageUrl}
          alt=""
          aria-hidden="true"
          style={{
            '--fly-start-x': `${item.startX}px`,
            '--fly-start-y': `${item.startY}px`,
            '--fly-delta-x': `${item.deltaX}px`,
            '--fly-delta-y': `${item.deltaY}px`,
          }}
        />
      ))}
      {dropNotifications.length > 0 && (
        <div className="drop-notifications-container mining-drop-notifications">
          {dropNotifications.map((notification) => (
            <div key={notification.id} className="drop-notification">
              {notification.message}
            </div>
          ))}
        </div>
      )}
      <AuthLayeredCard className="mining-card">
        {renderToolbar()}
        <ProfessionBadge skill="mining" />

        <div className="auth-selection-body mining-body">
          <div className="mining-stats-row">
            <div className="mining-stat-panel mining-stat-panel--inline" style={authStatPanelStyle}>
              <div className="mining-stat-panel__inline-row">
                <span className="mining-stat-panel__emoji mining-stat-panel__emoji--text">
                  {ts('miningPickaxeLevel')}
                </span>
                <span className="mining-stat-panel__value">{hasPickaxe ? `+${pickaxeEnh}` : '—'}</span>
              </div>
            </div>
            <div className="mining-stat-panel mining-stat-panel--inline" style={authStatPanelStyle}>
              <div className="mining-stat-panel__inline-row">
                <span className="mining-stat-panel__emoji" aria-hidden="true">⛏</span>
                <span className="mining-stat-panel__label">{ts('miningOreMined')}</span>
                <span className="mining-stat-panel__value">{oreMined}</span>
              </div>
            </div>
          </div>

          {(minerBookExpiry || masterBookExpiry) && (
            <div className="mining-book-hint">
              {minerBookExpiry && <div>{ts('miningMinerBookActive', { until: minerBookExpiry })}</div>}
              {masterBookExpiry && <div>{ts('miningMasterBookActive', { until: masterBookExpiry })}</div>}
            </div>
          )}

          <section className="mining-cave-section">
            <div
              className="mining-cave-frame"
              style={{
                backgroundImage: `url("${TEXTBOX_FRAME}")`,
                backgroundSize: '100% 100%',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            >
              <div className="mining-cave-visual-zoom">
                {renderMineVisual()}
              </div>
            </div>
            <div className="mining-status-panel mining-status-panel--under-visual" style={authStatPanelStyle}>
              <p className={`mining-status-text${!hasPickaxe && !statusMsg ? ' mining-status-text--warn' : ''}`}>
                {statusMsg
                  ? statusMsg
                  : !hasPickaxe && !sessionActive
                    ? ts('miningNoPickaxe')
                    : sessionActive && sessionPaused
                      ? ts('miningSessionPaused')
                      : sessionActive
                        ? ts('miningSessionActive')
                        : ts('miningIdleHint')}
              </p>
              {sessionActive && (
                <p className="mining-preview">
                  {ts('miningHitsPreview', {
                    afk: afkHitsPreview,
                    manual: manualHits,
                    total: totalHitsPreview,
                  })}
                </p>
              )}
            </div>
          </section>

          <div className="mining-session-panel">
            {sessionActive ? (
              <>
                <div className={`mining-timer${sessionPaused ? ' mining-timer--paused' : ''}`}>
                  {formatSeconds(sessionSecondsLeft)}
                </div>
                <div className="mining-actions">
                  {canMineNow && (
                    <button
                      type="button"
                      className={`auth-button mining-click-btn${clickCooldownUntil > Date.now() ? ' mining-click-btn--cooldown' : ''}`}
                      style={authButtonStyle}
                      data-silent-click="true"
                      disabled={busy || sessionSecondsLeft <= 0 || clickCooldownUntil > Date.now()}
                      onClick={handleClick}
                    >
                      {clickCooldownUntil > Date.now()
                        ? ts('miningClickWait', { sec: clickCooldownSec })
                        : ts('miningClick')}
                    </button>
                  )}
                  <button
                    type="button"
                    className="auth-button"
                    style={authButtonStyle}
                    disabled={busy}
                    onClick={handleClaim}
                  >
                    {busy ? tc('processing') : ts('miningClaim')}
                  </button>
                </div>
              </>
            ) : (
              <div className="mining-actions">
                <button
                  type="button"
                  className="auth-button"
                  style={authButtonStyle}
                  disabled={busy || !hasPickaxe || !miningLevelOk}
                  onClick={handleStart}
                >
                  {busy ? tc('processing') : ts('miningStart')}
                </button>
              </div>
            )}
          </div>

          {lastClaim?.drops?.length > 0 && (
            <div className="mining-drops-list">
              <strong>{ts('miningLastDrops')}</strong>
              {lastClaim.drops.map((d) => (
                <div key={d.otherItemId} className="mining-drop-item">
                  <span>{d.name}</span>
                  <span>x{d.count}</span>
                </div>
              ))}
              {lastClaim.miningXpGained > 0 && (
                <div className="mining-drop-item">
                  <span>{ts('miningXpGained')}</span>
                  <span>+{lastClaim.miningXpGained}</span>
                </div>
              )}
            </div>
          )}

          {config?.smelt && (
            <p className="mining-book-hint">
              {ts('miningSmeltHint', {
                count: smeltInputCount,
                rate: Math.round((config.smelt.successChance ?? 0.5) * 100),
              })}
            </p>
          )}
        </div>
      </AuthLayeredCard>
    </div>
  );
};

export default Mining;
